import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentDetails = ({ document, onUpdate }) => {
  const [editing, setEditing] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'indexed':
        return 'var(--color-success)';
      case 'processing':
        return 'var(--color-warning)';
      case 'failed':
        return 'var(--color-muted-foreground)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      indexed: 'Approved',
      processing: 'In Review',
      failed: 'Pending'
    };
    return labels?.[status] || status;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    return `${(bytes / 1024 / 1024)?.toFixed(2)} MB`;
  };

  const detailSections = [
    {
      title: 'Document Information',
      icon: 'FileText',
      items: [
        { label: 'Document Name', value: document?.name, icon: 'File' },
        { label: 'Document Type', value: document?.type?.toUpperCase(), icon: 'Tag' },
        { label: 'Format', value: document?.format || 'PDF', icon: 'FileType' },
        { label: 'File Size', value: formatFileSize(document?.file_size), icon: 'HardDrive' },
        { 
          label: 'Status', 
          value: getStatusLabel(document?.status),
          icon: 'CheckCircle',
          color: getStatusColor(document?.status)
        }
      ]
    },
    {
      title: 'Version Information',
      icon: 'GitBranch',
      items: [
        { label: 'Current Version', value: document?.metadata?.version || 'v1.0', icon: 'GitCommit' },
        { label: 'Last Modified', value: new Date(document?.updated_at)?.toLocaleString(), icon: 'Clock' },
        { label: 'Created', value: new Date(document?.created_at)?.toLocaleString(), icon: 'Calendar' },
        { label: 'Owner', value: document?.user_profiles?.full_name || 'Unknown', icon: 'User' }
      ]
    },
    {
      title: 'Project Information',
      icon: 'FolderTree',
      items: [
        { label: 'Project', value: document?.chat_projects?.name || 'N/A', icon: 'Folder' },
        { label: 'Project ID', value: document?.project_id?.substring(0, 8) + '...', icon: 'Hash' }
      ]
    }
  ];

  if (document?.metadata?.approval_required) {
    detailSections?.push({
      title: 'Approval Workflow',
      icon: 'CheckSquare',
      items: [
        { label: 'Approval Required', value: 'Yes', icon: 'AlertCircle', color: 'var(--color-warning)' },
        { label: 'Approval Status', value: 'Pending Review', icon: 'Clock', color: 'var(--color-warning)' }
      ]
    });
  }

  return (
    <div className="space-y-6">
      {detailSections?.map((section, idx) => (
        <div key={idx} className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
              <Icon name={section?.icon} size={18} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold">{section?.title}</h3>
          </div>

          <div className="space-y-3">
            {section?.items?.map((item, itemIdx) => (
              <div key={itemIdx} className="flex items-start gap-3 p-3 bg-background rounded-lg">
                <div className="flex items-center justify-center w-8 h-8 bg-muted rounded flex-shrink-0">
                  <Icon 
                    name={item?.icon} 
                    size={16} 
                    color={item?.color || 'var(--color-muted-foreground)'} 
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground caption mb-1">{item?.label}</p>
                  <p 
                    className="text-sm font-medium"
                    style={{ color: item?.color || 'var(--color-foreground)' }}
                  >
                    {item?.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {document?.metadata && Object?.keys(document?.metadata)?.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
              <Icon name="Code" size={18} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold">Metadata</h3>
          </div>

          <div className="p-4 bg-background rounded-lg">
            <pre className="text-xs text-muted-foreground overflow-x-auto">
              {JSON.stringify(document?.metadata, null, 2)}
            </pre>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button variant="outline" iconName="Edit" iconPosition="left">
          Edit Details
        </Button>
        <Button variant="outline" iconName="Trash2" iconPosition="left">
          Delete Document
        </Button>
      </div>
    </div>
  );
};

export default DocumentDetails;